import jwt from "jwt-decode";
import { createContext, useEffect, useRef } from "react";
import { v5 as uuidv5 } from 'uuid';

export const warning300 = "#ffe9cc";
export const warning500 = "#f5a73f";
export const alert300 = "#ffcccc";
export const alert500 = "#e50000";

export const CMX_TENANT_ID = "BB4D9AA3-3047-4542-8FB1-8C9BD6E4D75C";

export enum LogLevel {
  DEBUG = "DEBUG",
  INFO = "INFO",
  WARN = "WARN",
  ERROR = "ERROR",
}

export interface WidgetConfig {
  getBearerToken(): Promise<string>;
  theme?: any;
  logLevel?: LogLevel;
  bffURL?: string;
}

export const decodeToken = (token: string) => {
  return jwt(token);
};

export const isTokenValid = (token: any) => {
  if (!token) {
    return false;
  }
  const decodedPayload: any = jwt(token);
  const expiry = new Date(decodedPayload.exp * 1000);
  return expiry > new Date();
};

export interface WidgetOptions {
  onWorkItemSelected?: Function;
  prodTrackerActive?: boolean;
  userContext?: any;
}

export const columnWidth = (id, percentage) => {
  const div = document.getElementById(id); // HTML ID of Grid
  const parent = div?.parentElement;
  const width = (parent?.offsetWidth * percentage) / 100; // Pixel calculation
  return width;
};

export const collectionNamesQuery = (serviceLine,healthSystemId)=> {
  return {  query:
    `select name, collection_id as value from collection where type='LOCAL_COLLECTION' and status='ACTIVE' and service='${serviceLineMapper[serviceLine] ? serviceLineMapper[serviceLine]  : serviceLine}' and health_system_id='${healthSystemId}';`,
  }
};

export const policiesContext = createContext({
  policies: [],
  roles: [],
  userContext: {
    activeContext: { 
      serviceLines: [],
      healthSystemTenantId: "",
      tenantId: ""
     },
    serviceLine: { id: "", name: "" },
    user : {
      userId: ""
    }
  },
  coderAssignmentConfig: [],
});

export const useUpdateEffect: typeof useEffect = (effect, deps) => {
  const isFirstMount = useRef(true);

  useEffect(() => {
    if (!isFirstMount.current) effect();
    else isFirstMount.current = false;
  }, deps);
};

export const getResourceActions = (policies, roles, resource) => {
  const actions = [];

  policies.forEach((policy) => {
    policy?.data?.rules?.forEach((rule) => {
      const ruleResource = rule.resource.split(".")[0];

      // Check if the rule's resource matches the desired resource
      if (ruleResource === resource) {
        // Check if any of the rule's roles match the user's roles
        if (
          (rule.roles && rule?.roles?.some((role) => roles.includes(role))) ||
          rule?.roles?.some((role) => role === "*")
        ) {
          actions.push(...rule.actions);
        }
      }
    });
  });

  return actions;
};

const NAMESPACE = '0a0c6a78-dcc4-4b29-9d55-5a613d0d1a88';

export const generateKey = (filters) => {
  const filterString = JSON.stringify(filters);
  const key = uuidv5(filterString, NAMESPACE);
  return key;
};

export const dateFormatOptions = {
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
} as Intl.DateTimeFormatOptions;

export const WidgetConstants = {
  WORKLIST: 'Worklist',
  CODING_QUALITY: 'Coding Quality',
  QUALITY_ASSESSMENT: 'Quality Assessment',
  WORKITEM: 'workitem',
  QA: 'qa',
  CQA: "quality_assessment",
  COLLECTION: 'collection'
}

export const isArrayWithNull = (value) => {
  return Array.isArray(value) && value.length === 1 && value[0] === null;
}

export const dateTimeFormatter = (date, targetTimezone) => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: targetTimezone,
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true, // Use 24-hour format
    timeZoneName: "short",
  });
  // Format the date to display the time and timezone abbreviation in the target timezone
  return formatter.format(date);
}

export const serviceLineMapper = {
  "Bedside Pro": "IPC",
  "Surgery": "SURGERY",
  "Radiology": "RADIOLOGY",
};

export const inverseServiceLineMapper = {
    "IPC": "Bedside Pro",
    "SURGERY": "Surgery",
    "RADIOLOGY": "Radiology",
};

export const reverseServiceLineMapper = {
  "Bedside Pro": "Inpatient Professional Coding"
};

export const updateTokeninSessionStorage = (token) => {
  const authToken = token.includes("Bearer ") ? token.split(" ")[1] : token;
  if (authToken !== sessionStorage.getItem("authToken"))
    sessionStorage.setItem("authToken", authToken);
};

export const USER_ROLES = {
  PROFESSIONAL_SERVICES: "professional_services"
};

export const cqaWorklistSearchFieldOptions = {
  defaultFilterOption: "workitem.case_id",
  availableOptions: [
    {
      label: "Case ID",
      key: "workitem.case_id",
    },
    {
      label: "CQA WorkItem ID",
      key: "cqa_workitem_id",
    },
    {
      label: "Import ID",
      key: "import_id",
    },
  ],
};

export const safeJsonStringify = (arg) => {
  return JSON.stringify(arg || {});
}
